import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { BankAccountSummary } from '@/types/accounts';
import { ConsentRenewalDialog, type ConsentRenewalTarget } from '@/components/cash/ConsentRefreshDialog';

type ConsentRequiredError = ConsentRenewalTarget;

interface ConsentContextType {
  consentRequiredError: ConsentRequiredError | null;
  setConsentRequiredError: (error: ConsentRequiredError | null) => void;
  handleConsentRefreshSuccess: (accounts: BankAccountSummary[]) => void;
  /** The account list the latest successful renewal fetched; null until one succeeds. Every
   *  page-held account list applies it through useAccountsAfterRenewal (FIN-885). */
  renewedAccounts: BankAccountSummary[] | null;
  // Optional callback for when accounts are refreshed - can be set by specific pages
  onAccountsRefresh?: (accounts: BankAccountSummary[]) => void;
  setOnAccountsRefresh: (callback: ((accounts: BankAccountSummary[]) => void) | undefined) => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export function useConsentContext() {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsentContext must be used within a ConsentProvider');
  }
  return context;
}

// Optional version that doesn't throw an error
export function useConsentContextOptional() {
  const context = useContext(ConsentContext);
  return context; // Returns undefined if not in provider
}

interface ConsentProviderProps {
  children: React.ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [consentRequiredError, setConsentRequiredError] = useState<ConsentRequiredError | null>(null);
  const [onAccountsRefresh, setOnAccountsRefresh] = useState<((accounts: BankAccountSummary[]) => void) | undefined>(undefined);
  const [renewedAccounts, setRenewedAccounts] = useState<BankAccountSummary[] | null>(null);

  const handleConsentRefreshSuccess = useCallback((updatedAccounts: BankAccountSummary[]) => {
    console.log("ConsentProvider: Consent refresh successful, clearing error and updating accounts:", updatedAccounts);
    setConsentRequiredError(null);
    setRenewedAccounts(updatedAccounts);
    onAccountsRefresh?.(updatedAccounts);
  }, [onAccountsRefresh]);

  const clearConsentError = useCallback(() => {
    console.log("ConsentProvider: Clearing consent error");
    setConsentRequiredError(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    consentRequiredError,
    setConsentRequiredError,
    handleConsentRefreshSuccess,
    renewedAccounts,
    onAccountsRefresh,
    setOnAccountsRefresh
  }), [consentRequiredError, setConsentRequiredError, handleConsentRefreshSuccess, renewedAccounts, onAccountsRefresh, setOnAccountsRefresh]);

  console.log("🌍 ConsentProvider render - consentRequiredError:", consentRequiredError);

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}

      {/* Global Consent Refresh Dialog */}
      {consentRequiredError && (
        <>
          {console.log("🚀 ConsentProvider: Rendering global ConsentRefreshDialog with:", consentRequiredError)}
          <ConsentRenewalDialog target={consentRequiredError} onClose={clearConsentError} onSuccess={handleConsentRefreshSuccess} />
        </>
      )}
    </ConsentContext.Provider>
  );
}